import { User } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FilterButton } from '@/components/ui/list-filters';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { MemberSelect, useMemberUserLabel } from '@/features/members';

const OWNER_PICKER_KEY = 'bids-owner-filter';

/**
 * Owner filter for the Bids toolbar: Anyone · Me · a specific bidder. "Me" is
 * the backend's `mine` param; a picked bidder is `owner` — the two never combine.
 */
export function OwnerFilter({
  mine,
  owner,
  onChange,
}: {
  mine: boolean;
  owner: string;
  onChange: (next: { mine: boolean; owner: string }) => void;
}) {
  const { data: ownerName } = useMemberUserLabel(owner);
  const summary = mine ? 'Me' : owner ? (ownerName ?? 'Selected bidder') : 'Anyone';

  return (
    <FilterButton
      label="Owner"
      value={summary}
      active={mine || !!owner}
      icon={<User className="size-4" />}
    >
      <div className="flex gap-1.5">
        <Button
          size="xs"
          variant={!mine && !owner ? 'default' : 'outline'}
          onClick={() => onChange({ mine: false, owner: '' })}
        >
          Anyone
        </Button>
        <Button
          size="xs"
          variant={mine ? 'default' : 'outline'}
          onClick={() => onChange({ mine: true, owner: '' })}
        >
          Me
        </Button>
      </div>
      <div className="space-y-1">
        <span className="text-xs font-medium">A specific bidder</span>
        <MemberSelect
          value={owner}
          onChange={(v) => onChange({ mine: false, owner: v })}
          placeholder="Pick a bidder"
          queryKey={OWNER_PICKER_KEY}
          roles={CRM_OWNER_ROLES}
          clearable
          triggerClassName="h-8 w-full"
        />
      </div>
    </FilterButton>
  );
}
